import {
 PagedResponse,
 PatchWithMeta,
 QueryParams,
 ValidatedResponse,
} from '@ofqual/portal-core/core/types';
import {
 all,
 call,
 getContext,
 put,
 select,
 takeEvery,
 takeLatest,
} from 'redux-saga/effects';
import { MODULE_NAME } from '../moduleName';
import { lcmService } from '../services';
import {
 ActionPayload,
 CREATED_ITEM,
 CREATING_ITEM,
 CREATING_ITEM_VALIDATION_FAIL,
 loadedItem,
 loadedItems,
 LOADING_ITEM,
 LOADING_ITEMS,
 savedItem,
 UPDATING_ITEM,
 UPDATING_ITEM_VALIDATION_FAIL,
 REQUESTING_EXTERNAL_COUNSEL,
 loadingItem,
 REJECTING_EXTERNAL_COUNSEL,
 ACCEPTING_EXTERNAL_COUNSEL,
 ADD_TIME_RECORD,
 ADD_CASE_NOTE,
 ATTACHING_EMAIL,
 ADD_INTERNAL_MESSAGE,
 UPDATE_LCM_STATUS,
 CREATE_FUTURE_EVENT,
} from './actions';
import { authSelector } from './selectors';
import { History } from 'history';
import { LcmModel, LcmStatus } from '../domain/LcmModel';
import { refreshActivityFeed } from '@ofqual/portal-activity-feed';
import { LcmTimeRecord } from '../domain/LcmTimeRecord';
import { CaseNoteModel } from '../domain/CaseNoteModel';
import { InternalMessageModel } from '../domain/InternalMessageModel';
import { CommunicationType } from '../domain/FutureEventModel';

function* navigate(url: string) {
 const history: History = yield getContext('history');
 yield call(() => history.push(url));
}

function* watchItemsSaga() {
 yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
 yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchUpdateItemSaga() {
 yield takeEvery(UPDATING_ITEM, updateItemSaga);
}

function* watchCreateItemSaga() {
 yield takeEvery(CREATING_ITEM, createItemSaga);
}

function* watchRequestingExternalCounselSaga() {
 yield takeEvery(REQUESTING_EXTERNAL_COUNSEL, requestingExternalCounselSaga);
}

function* watchRejectingExternalCounselSaga() {
 yield takeEvery(REJECTING_EXTERNAL_COUNSEL, rejectingExternalCounselSaga);
}

function* watchAcceptingExternalCounselSaga() {
 yield takeEvery(ACCEPTING_EXTERNAL_COUNSEL, acceptingExternalCounselSaga);
}

function* watchAddTimeRecordSaga() {
 yield takeLatest(ADD_TIME_RECORD, addTimeRecordSaga);
}

function* watchAddCaseNoteSaga() {
 yield takeLatest(ADD_CASE_NOTE, addCaseNoteSaga);
}

function* watchAttachEmailSaga() {
 yield takeLatest(ATTACHING_EMAIL, attachEmailSaga);
}

function* watchAddInternalMessageSaga() {
 yield takeLatest(ADD_INTERNAL_MESSAGE, addInternalMessageSaga);
}

function* watchUpdateLegalCaseStatusSaga() {
 yield takeLatest(UPDATE_LCM_STATUS, updateLegalCaseStatusSaga);
}

function* watchCreateFutureEventSaga() {
 yield takeLatest(CREATE_FUTURE_EVENT, createFutureEventSaga);
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const items: PagedResponse<LcmModel> = yield call(
  [lcmService, lcmService.search],
  { ...payload },
  { token: auth.token },
 );
 yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<string>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const lcm: LcmModel = yield call([lcmService, lcmService.get], payload, {
  token: auth.token,
 });
 yield put(loadedItem(lcm));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { id, patchWithMeta } = payload as {
  id: string;
  patchWithMeta: PatchWithMeta<LcmModel>;
 };

 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.update],
  id,
  patchWithMeta,
  {
   token: auth.token,
  },
 );
 if (validatedResponse.isValid) {
  yield put(savedItem(validatedResponse.data));
  yield put(refreshActivityFeed(id));
 } else {
  yield put({
   type: UPDATING_ITEM_VALIDATION_FAIL,
   payload: validatedResponse.error,
  });
 }
}

function* createItemSaga({ payload }: ActionPayload<LcmModel>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.create],
  payload,
  {
   token: auth.token,
  },
 );
 if (validatedResponse.isValid) {
  yield put({ type: CREATED_ITEM });
  yield call(() => navigate(`/${MODULE_NAME}/${validatedResponse.data.id}`));
 } else {
  yield put({
   type: CREATING_ITEM_VALIDATION_FAIL,
   payload: validatedResponse.error,
  });
 }
}

function* requestingExternalCounselSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { id, req } = payload as {
  id: string;
  req: LcmModel;
 };
 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.requestExternalCounsel],
  id,
  req,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(id));
  yield put(loadingItem(id));
 }
}

function* rejectingExternalCounselSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { lcmId, lcmExternalCounselRequestId } = payload as {
  lcmId: string;
  lcmExternalCounselRequestId: string;
 };

 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.rejectExternalCounsel],
  lcmId,
  lcmExternalCounselRequestId,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(lcmId));
  yield put(loadingItem(lcmId));
 }
}

function* acceptingExternalCounselSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { lcmId, lcmExternalCounselRequestId } = payload as {
  lcmId: string;
  lcmExternalCounselRequestId: string;
 };

 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.acceptExternalCounsel],
  lcmId,
  lcmExternalCounselRequestId,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(lcmId));
  yield put(loadingItem(lcmId));
 }
}

function* updateLegalCaseStatusSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { lcmId, lcmStatus, caseNote } = payload as {
  lcmId: string;
  lcmStatus: LcmStatus;
  caseNote: string;
 };
 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.updateLcmStatus],
  lcmId,
  lcmStatus,
  caseNote,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(lcmId));
  yield put(loadingItem(lcmId));
 }
}

function* createFutureEventSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { lcmId, dateTime, eventType, communicationType, note } = payload as {
  lcmId: string;
  dateTime: Date;
  eventType: string;
  communicationType: CommunicationType;
  note: string;
 };
 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.createFutureEvent],
  lcmId,
  dateTime,
  eventType,
  communicationType,
  note,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(lcmId));
  yield put(loadingItem(lcmId));
 }
}

function* addTimeRecordSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { lcmId, timeRecord } = payload as {
  lcmId: string;
  timeRecord: LcmTimeRecord;
 };

 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.addTimeRecord],
  lcmId,
  timeRecord,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(lcmId));
  yield put(loadingItem(lcmId));
 }
}

function* addCaseNoteSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { lcmId, caseNote } = payload as {
  lcmId: string;
  caseNote: CaseNoteModel;
 };

 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.addCaseNote],
  lcmId,
  caseNote.note,
  caseNote.noteType,
  caseNote.meetingDate,
  caseNote.documents,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(lcmId));
  yield put(loadingItem(lcmId));
 }
}

function* attachEmailSaga({
 payload,
}: ActionPayload<{ id: string; file: File }>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);
 const { id, file } = payload;

 yield call([lcmService, lcmService.attachEmail], id, file, {
  token: auth.token,
 });

 yield put(refreshActivityFeed(id));
}

function* addInternalMessageSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { lcmId, internalMessage } = payload as {
  lcmId: string;
  internalMessage: InternalMessageModel;
 };

 const validatedResponse: ValidatedResponse<LcmModel> = yield call(
  [lcmService, lcmService.addInternalMessage],
  lcmId,
  internalMessage,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(lcmId));
  yield put(loadingItem(lcmId));
 }
}

export function* rootSaga() {
 yield all([
  watchItemsSaga(),
  watchItemSaga(),
  watchUpdateItemSaga(),
  watchCreateItemSaga(),
  watchRequestingExternalCounselSaga(),
  watchRejectingExternalCounselSaga(),
  watchAcceptingExternalCounselSaga(),
  watchAddTimeRecordSaga(),
  watchAddCaseNoteSaga(),
  watchAttachEmailSaga(),
  watchAddInternalMessageSaga(),
  watchUpdateLegalCaseStatusSaga(),
  watchCreateFutureEventSaga(),
  // further sagas...
 ]);
}
